<template>
  <div class="order-payment-info">
    <p class="section-title text-primary-100 font-medium">{{ $t('orderPaymentBlock.title' ) }}</p>
    <BaseElForm label-width="150px" label-position="left">
      <!-- <BaseElFormItem label="付款狀態">
        <div>
          <Tag style="display: inline-flex" :type="tagType(orderDepositData.status)">
            {{ paymentStatus }}
          </Tag>
          <BaseElButton v-if="showRefundBtn" type="text" class="refund-btn" @click="goRefund">
            前往退款
          </BaseElButton>
        </div>
      </BaseElFormItem>
      <BaseElFormItem label="應付定金">
        {{ depositTotalPrice }}
      </BaseElFormItem>
      <BaseElFormItem v-if="orderDepositData.status === 'refund'" label="退款金額">
        <span v-if="orderDepositData.refundPrice">$ {{ orderDepositData.refundPrice }}</span>
        <span v-else>-</span>
      </BaseElFormItem>
      <BaseElFormItem label="付款方式">
        {{ depositPaymentType }}
      </BaseElFormItem>
      <BaseElFormItem v-if="showPaymentCode" label="交易序號">
        <div class="flex items-center" style="gap: 14px">
          <p>{{ orderDepositData.code }}</p>
          <div class="flex items-center" @click="copyPaymentCode">
            <MaterialIcon color="var(--primary-100)" size="24">content_copy_filled</MaterialIcon>
          </div>
        </div>
      </BaseElFormItem> -->

      <BaseElFormItem v-for="struct in dataStruct" :key="struct.key" :label="struct.label">
        <div v-if="struct.items" class="flex items-center" :style="`gap: ${struct.itemsGap}px; min-height: 40px`">
          <template v-for="(item, idx) in struct.items">
            <p v-if="item.el === 'p'" :key="`item-${idx}`">
              {{ item.label }}
            </p>
            <Tag
              v-if="item.el === 'tag'"
              :key="`item-${idx}`"
              class="inline-flex"
              v-bind="item.props"
            >
              {{ item.label }}
            </Tag>

            <div
              v-if="item.el === 'materialIcon'"
              :key="`item-${idx}`"
              class="cursor-pointer"
              v-on="item.on"
            >
              <MaterialIcon color="var(--primary-100)" size="24">
                {{ item.icon }}
              </MaterialIcon>
            </div>

            <BaseElButton
              v-if="item.el === 'el-button' && item.if"
              :key="`item-${idx}`"
              v-bind="item.props"
              v-on="item.on"
            >
              {{ $t('orderPaymentBlock.button.refund.title') }}
            </BaseElButton>
          </template>
        </div>
      </BaseElFormItem>
    </BaseElForm>

    <div v-if="showMarkRefundBtn" class="flex justify-end">
      <BaseElButton plain @click="showMarkRefundDialog = true">{{ $t('orderPaymentBlock.button.markRefund.title') }}</BaseElButton>
    </div>

    <DepositRefundDialog
      v-if="showRefundDialog"
      :type="refundDialogType"
      :isManual="manualRefund"
      :orderId="orderData.id"
      :payment="{
        type: orderDepositData.paidType,
        comment: orderDepositData.paidTypeComment,
      }"
      @refunded="$emit('refresh')"
      @close="showRefundDialog = false"
    />
    <MarkRefundDialog
      v-if="showMarkRefundDialog"
      :orderData="orderData"
      :data="refundData"
      :maxPrice="orderDepositData.depositPrice || null"
      chargeType="deposit"
      @marked="$emit('refresh')"
      @close="showMarkRefundDialog = false"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import copy from 'clipboard-copy'
import { get, includes, filter } from 'lodash'
import { depositPaymentTypes, depositStatusConfig } from '@/config/deposit'
import DepositRefundDialog from './DepositRefundDialog.vue'
import MarkRefundDialog from './MarkRefundDialog.vue'
import notifyMessage from '@/config/notifyMessage'
import { useExternalWallet } from '@/use/useExternalWallet'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'OrderPaymentBlock',
  components: {
    DepositRefundDialog,
    MarkRefundDialog,
  },
  props: {
    reservation: {
      type: Object,
      default: () => ({}),
    },
    index: Number,
  },
  setup (props) {
    const { externalWalletName, getExternalWalletConfig } = useExternalWallet()
    const manualRefund = ref(true)
    const showRefundDialog = ref(false)
    const refundDialogType = ref('wallet')
    const showMarkRefundDialog = ref(false)

    const refundData = computed(() => ({
      refundType: get(orderDepositData.value, 'paidType'),
      refundTypeComment: get(orderDepositData.value, 'paidTypeComment'),
    }))

    const orderData = computed(() => get(props.reservation, 'AppointmentOrder') || {})

    const orderTotalPrice = computed(() => {
      const price = orderData.value.totalPrice
      if (!price) return '-'
      return `$ ${price}`
    })

    const orderDepositData = computed(() => get(props.reservation, 'AppointmentOrder.AppointmentOrderDeposit'))

    const depositTotalPrice = computed(() => {
      const price = orderDepositData.value.depositPrice
      if (!price) return '-'
      return `$ ${price}`
    })

    const depositPaymentType = computed(() => {
      const type = orderDepositData.value.paidType
      if (type === 'offline') {
        const comment = orderDepositData.value.paidTypeComment
        return i18n.t(get(depositPaymentTypes[comment], 'name'))
      }
      if (type === 'externalWallet') return externalWalletName.value
      return i18n.t(get(depositPaymentTypes[type], 'name'))
    })

    const paymentStatus = computed(() => {
      const status = orderDepositData.value.status
      const paidType = orderDepositData.value.paidType
      // 當付款方式為免付定金且狀態為已付款時,顯示「免付定金」
      const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
      return i18n.t(get(depositStatusConfig[displayStatus], 'name'))
    })

    const showRefundBtn = computed(() => {
      const status = orderDepositData.value.status
      const paymentType = orderDepositData.value.paidType
      const allowType = ['jkos', 'linepay', 'newebpay', 'wallet']
      if (status === 'waitRefund') {
        if (allowType.includes(paymentType)) return true
      }
      return false
    })

    const showMarkRefundBtn = computed(() => {
      const status = orderDepositData.value.status
      const paymentType = orderDepositData.value.paidType
      if (paymentType === 'wallet' && status === 'waitRefund') return false
      if (status === 'waitRefund') return true
      return false
    })

    const showPaymentCode = computed(() => {
      const paymentType = orderDepositData.value.paidType
      if (paymentType === 'offline') return false
      if (paymentType === 'free') return false
      const payment = depositPaymentTypes[paymentType]
      if (payment.type === 'third-part') return true
      return false
    })

    const goRefund = () => {
      const type = orderDepositData.value.paidType
      const other = ['jkos', 'newebpay', 'linepay']
      if (type === 'wallet') refundDialogType.value = 'wallet'
      if (type === 'externalWallet') refundDialogType.value = 'externalWallet'
      if (other.includes(type)) refundDialogType.value = 'other'
      if (type.includes('offline')) refundDialogType.value = 'mark'
      showRefundDialog.value = true
    }

    const tagType = (status) => {
      const paidType = orderDepositData.value.paidType
      // 當付款方式為免付定金且狀態為已付款時,使用 free 狀態的 tag
      const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
      return get(depositStatusConfig[displayStatus], 'tag')
    }

    const copyPaymentCode = () => {
      copy(orderDepositData.value.code)
      window.$message.success(notifyMessage.copySuccess)
    }

    const isBatchOrder = computed(() => {
      return get(props.reservation, 'AppointmentOrder.AppointmentBatchOrder')
    })

    const dataStruct = computed(() => {
      // 所有欄位與資料
      const data = [
        {
          label: i18n.t('orderPaymentBlock.status.title'),
          key: 'status',
          itemsGap: 8,
          items: [
            {
              el: 'tag',
              props: {
                type: tagType(orderDepositData.value.status),
                disableTransitions: true,
              },
              label: paymentStatus.value,
            },
            {
              el: 'el-button',
              props: { type: 'text', class: 'refund-btn underline' },
              if: showRefundBtn.value,
              on: {
                click: goRefund,
              },
              label: i18n.t('orderPaymentBlock.button.refund.title'),
            },
          ],
        },
        {
          label: i18n.t('orderPaymentBlock.totalItemsPrice.title'),
          key: 'totalItemsPrice',
          items: [
            { el: 'p', label: depositTotalPrice.value },
          ],
        },
        {
          label: i18n.t('orderPaymentBlock.refundPrice.title'),
          key: 'refundPrice',
          items: [
            {
              el: 'p',
              label: orderDepositData.value.refundPrice ? `$ ${orderDepositData.value.refundPrice}` : '-',
            },
          ],
        },
        {
          label: i18n.t('orderPaymentBlock.paidType.title'),
          key: 'paidType',
          items: [
            { el: 'p', label: depositPaymentType.value },
          ],
        },
        {
          label: i18n.t('orderPaymentBlock.orderCode.title'),
          key: 'orderCode',
          itemsGap: 8,
          items: [
            {
              el: 'p',
              label: orderDepositData.value.code,
            },
            {
              el: 'materialIcon',
              icon: 'content_copy_filled',
              on: {
                click: () => {
                  try {
                    copy(orderDepositData.value.code)
                    window.$message.success(notifyMessage.copySuccess)
                  } catch (error) {
                    window.$message.error(error)
                  }
                },
              },
            },
          ],
        },
      ]

      // 依據條件顯示不同的欄位
      if (isBatchOrder.value) {
        return filter(data, (item) => includes(['totalItemsPrice'], item.key))
      }
      return data
    })
    onMounted(() => {
      getExternalWalletConfig()
    })
    return {
      dataStruct,
      manualRefund,
      showRefundDialog,
      refundDialogType,
      showMarkRefundDialog,
      refundData,
      orderData,
      orderTotalPrice,
      orderDepositData,
      depositTotalPrice,
      depositPaymentType,
      paymentStatus,
      showRefundBtn,
      showMarkRefundBtn,
      showPaymentCode,
      goRefund,
      tagType,
      copyPaymentCode,
    }
  },
})
</script>

<style lang="postcss" scoped>
.refund-btn {
  @apply ml-[11px];
}
</style>
